interface AnalyticAccountSuggestion
{
    account:AnalyticAccount;
    matches:number;
    score?:number;
}

// Suggests analytic accounts for uncategorized bank movements from the ones already
// categorized. Match tiers, strongest first: beneficiary IBAN, beneficiary name,
// concept fingerprint and finally token overlap between concepts.
class AnalyticAccountSuggester extends MIOObject
{
    private accountsByID = {};
    private ibanIndex = {};
    private beneficiaryIndex = {};
    private conceptIndex = {};
    private tokenDocs = [];
    private tokenIndex = {};   // token -> { accountID: count }
    private tokenTotals = {};  // token -> total occurrences in categorized movements

    // Strip digits and punctuation so "COMPRA TARJ. 4552XX MERCADONA 12/07" and
    // "COMPRA TARJ. 4552XX MERCADONA 15/07" share the same fingerprint
    static conceptFingerprint(text:string) : string {
        if (text == null) return null;
        let t = text.toUpperCase();
        t = t.replace(/[^A-ZÁÉÍÓÚÜÑÇ]+/g, " ").trim();
        return t.length > 0 ? t : null;
    }

    private static normalizedKey(text:string) : string {
        if (text == null) return null;
        let t = text.toUpperCase().replace(/\s+/g, " ").trim();
        return t.length > 0 ? t : null;
    }

    reload(target:any, completion:any) {
        let predicate = MIOPredicate.predicateWithFormat("analyticAccountName != null");
        DBHelper.queryObjectsWithCompletion("BankMovement", null, predicate, ["analyticAccount"], this, function(this:AnalyticAccountSuggester, objects:BankMovement[]){
            this.accountsByID = {};
            this.ibanIndex = {};
            this.beneficiaryIndex = {};
            this.conceptIndex = {};
            this.tokenDocs = [];
            this.tokenIndex = {};
            this.tokenTotals = {};
            for (let index = 0; index < objects.length; index++) this.learn(objects[index]);
            if (completion != null) completion.call(target);
        });
    }

    learn(movement:BankMovement) {
        let account = movement.analyticAccount;
        if (account == null) return;
        this.accountsByID[account.identifier] = account;

        this.addToIndex(this.ibanIndex, AnalyticAccountSuggester.normalizedKey(movement.beneficiaryIBAN), account);
        this.addToIndex(this.beneficiaryIndex, AnalyticAccountSuggester.normalizedKey(movement.beneficiaryName), account);
        this.addToIndex(this.conceptIndex, this.conceptKey(movement), account);

        let tokens = this.tokensFromConcept(movement.concept);
        if (tokens != null) {
            this.tokenDocs.push({ tokens: tokens, accountID: account.identifier });
            for (let word in tokens.map) {
                if (this.tokenIndex[word] == null) this.tokenIndex[word] = {};
                this.tokenIndex[word][account.identifier] = (this.tokenIndex[word][account.identifier] || 0) + 1;
                this.tokenTotals[word] = (this.tokenTotals[word] || 0) + 1;
            }
        }
    }

    suggestionFor(movement:BankMovement, allowFuzzy:boolean = true) : AnalyticAccountSuggestion {
        if (movement == null) return null;

        let suggestion = this.bestFromIndex(this.ibanIndex, AnalyticAccountSuggester.normalizedKey(movement.beneficiaryIBAN));
        if (suggestion == null) suggestion = this.bestFromIndex(this.beneficiaryIndex, AnalyticAccountSuggester.normalizedKey(movement.beneficiaryName));
        if (suggestion == null) suggestion = this.bestFromIndex(this.conceptIndex, this.conceptKey(movement));
        if (suggestion != null || allowFuzzy == false) return suggestion;

        suggestion = this.fuzzySuggestion(movement);
        if (suggestion != null) return suggestion;

        // Keyword tier: a distinctive word (e.g. the payee name) shared with categorized
        // movements even when the rest of the concept differs. Requires 2 supporting
        // movements to avoid one-off coincidences.
        let candidates = this.keywordCandidates(movement);
        return candidates.length > 0 && candidates[0].matches >= 2 ? candidates[0] : null;
    }

    // Other movements that could belong to the same analytic account as the source.
    // Exact matches score highest, then concept overlap, then a shared distinctive
    // keyword (e.g. the payee name inside an otherwise different concept).
    similarCandidates(source:BankMovement, movements:any[]) : BankMovement[] {
        if (source == null) return [];

        let sourceTokens = this.tokensFromConcept(source.concept);

        let scored = [];
        for (let index = 0; index < movements.length; index++){
            let mv = movements[index];
            if ((mv instanceof BankMovement) == false) continue;
            if (mv == source) continue;
            if (BankMovementState.isCategorized(mv)) continue;

            let score = 0;
            if (this.isSimilar(source, mv as BankMovement)) {
                score = 3;
            }
            else if (sourceTokens != null) {
                let candidateTokens = this.tokensFromConcept((mv as BankMovement).concept);
                if (candidateTokens != null) {
                    let common = 0;
                    let distinctive = false;
                    for (let word in sourceTokens.map) {
                        if (candidateTokens.map[word] == null) continue;
                        common++;
                        if (this.isGenericToken(word) == false) distinctive = true;
                    }
                    let union = sourceTokens.count + candidateTokens.count - common;
                    if (union > 0 && common / union >= 0.5) score = 2;
                    else if (distinctive) score = 1;
                }
            }

            if (score > 0) scored.push({ movement: mv as BankMovement, score: score });
        }

        scored.sort(function(a, b){ return b.score - a.score; });

        let result:BankMovement[] = [];
        for (let index = 0; index < scored.length; index++) result.push(scored[index].movement);
        return result;
    }

    private isGenericToken(word:string) : boolean {
        let counts = this.tokenIndex[word];
        if (counts == null) return word.length < 4; // unknown to history: short words are noise
        let distinctAccounts = 0;
        for (let id in counts) distinctAccounts++;
        return distinctAccounts > 5; // spread over many accounts (RECIBO, TRANSFERENCIA...)
    }

    private keywordCandidates(movement:BankMovement) : AnalyticAccountSuggestion[] {
        let tokens = this.tokensFromConcept(movement.concept);
        if (tokens == null) return [];

        let scores = {};
        let matches = {};
        for (let word in tokens.map) {
            let counts = this.tokenIndex[word];
            if (counts == null) continue;
            if (this.isGenericToken(word)) continue;

            for (let id in counts) {
                // specificity: how exclusively this word belongs to this account
                scores[id] = (scores[id] || 0) + counts[id] / this.tokenTotals[word];
                if ((matches[id] || 0) < counts[id]) matches[id] = counts[id];
            }
        }

        let candidates:AnalyticAccountSuggestion[] = [];
        for (let id in scores) candidates.push({ account: this.accountsByID[id], matches: matches[id], score: scores[id] });
        candidates.sort(function(a, b){ return b.score - a.score; });

        return candidates;
    }

    // Exact tiers only. Used to propagate an assignment to other movements safely.
    isSimilar(a:BankMovement, b:BankMovement) : boolean {
        let iban = AnalyticAccountSuggester.normalizedKey(a.beneficiaryIBAN);
        if (iban != null && iban == AnalyticAccountSuggester.normalizedKey(b.beneficiaryIBAN)) return true;

        let beneficiary = AnalyticAccountSuggester.normalizedKey(a.beneficiaryName);
        if (beneficiary != null && beneficiary == AnalyticAccountSuggester.normalizedKey(b.beneficiaryName)) return true;

        let concept = this.conceptKey(a);
        return concept != null && concept == this.conceptKey(b);
    }

    // Income and expenses with the same text should not share suggestions
    private conceptKey(movement:BankMovement) : string {
        let fingerprint = AnalyticAccountSuggester.conceptFingerprint(movement.concept);
        if (fingerprint == null) return null;
        return (movement.amount < 0 ? "-" : "+") + fingerprint;
    }

    private addToIndex(index:any, key:string, account:AnalyticAccount) {
        if (key == null) return;
        if (index[key] == null) index[key] = {};
        index[key][account.identifier] = (index[key][account.identifier] || 0) + 1;
    }

    private bestFromIndex(index:any, key:string) : AnalyticAccountSuggestion {
        if (key == null) return null;
        let counts = index[key];
        if (counts == null) return null;

        let bestID = null;
        let bestCount = 0;
        for (let id in counts) {
            if (counts[id] > bestCount) { bestCount = counts[id]; bestID = id; }
        }

        return bestID != null ? { account: this.accountsByID[bestID], matches: bestCount } : null;
    }

    private tokensFromConcept(concept:string) {
        let fingerprint = AnalyticAccountSuggester.conceptFingerprint(concept);
        if (fingerprint == null) return null;

        let map = {};
        let count = 0;
        let words = fingerprint.split(" ");
        for (let index = 0; index < words.length; index++){
            if (words[index].length < 3) continue;
            if (map[words[index]] == null) { map[words[index]] = true; count++; }
        }

        return count > 0 ? { map: map, count: count } : null;
    }

    private fuzzySuggestion(movement:BankMovement) : AnalyticAccountSuggestion {
        let tokens = this.tokensFromConcept(movement.concept);
        if (tokens == null) return null;

        let counts = {};
        for (let index = 0; index < this.tokenDocs.length; index++){
            let doc = this.tokenDocs[index];
            let common = 0;
            for (let word in tokens.map) {
                if (doc.tokens.map[word] != null) common++;
            }
            let union = tokens.count + doc.tokens.count - common;
            if (union == 0) continue;
            if (common / union >= 0.6) counts[doc.accountID] = (counts[doc.accountID] || 0) + 1;
        }

        let bestID = null;
        let bestCount = 0;
        for (let id in counts) {
            if (counts[id] > bestCount) { bestCount = counts[id]; bestID = id; }
        }

        return bestID != null ? { account: this.accountsByID[bestID], matches: bestCount } : null;
    }
}
